import { useParams } from "react-router-dom";

import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../../shared/constants/routes";
import StatusMessage from "../../../shared/components/elements/StatusMessage";
import PageTitle from "../../../shared/components/layout/PageTitle";
import SEO from "../../../shared/components/layout/SEO";
import CheatSheetCard from "../components/CheatSheetCard"; // Move into this feature next
import AccountLayout from "../../account/components/AccountLayout";
import { ACCOUNT_MESSAGES } from "../../account/messages";

import { useCheatsheets } from "../hooks/useCheatsheets";
import { CHEATSHEET_MESSAGES } from "../messages";

const CheatsheetListPage = () => {
    const { course_id } = useParams<{ course_id: string }>();
    const { lang, t } = useLanguage();
    const { data, loading, error } = useCheatsheets(course_id);

    const courseTitle = data?.title ?? "";

    const breadcrumb = [
        { title: ACCOUNT_MESSAGES.account, link: "#" },
        { title: ACCOUNT_MESSAGES.myCourses, link: ROUTES.myCourses(lang) },
        // The course title only exists once the request has finished
        ...(course_id && courseTitle
            ? [{ title: courseTitle, link: ROUTES.myCourseContent(lang, course_id) }]
            : []),
        { title: CHEATSHEET_MESSAGES.cheatsheet, link: "" }
    ];

    const renderContent = () => {
        if (loading) return <StatusMessage>{CHEATSHEET_MESSAGES.loading}</StatusMessage>;
        if (error || !data) return <StatusMessage>{CHEATSHEET_MESSAGES.listFailed}</StatusMessage>;
        if (data.cheatsheets.length === 0) {
            return (
                <div className="bg-gray-100 rounded-xl p-8 text-center text-gray-500">
                    {CHEATSHEET_MESSAGES.listEmpty}
                </div>
            );
        }

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                {data.cheatsheets.map((cheatsheet) => (
                    <CheatSheetCard key={cheatsheet.id} cheatsheet={cheatsheet} courseId={course_id} />
                ))}
            </div>
        );
    };

    return (
        <>
            {/* Private page: keep it out of search results */}
            <SEO
                title={`${CHEATSHEET_MESSAGES.cheatsheet} | ${t.nav.brand}`}
                description={CHEATSHEET_MESSAGES.seoDescription}
                path={`/account/course/${course_id}/cheatsheet`}
                noIndex
            />

            <AccountLayout breadcrumb={breadcrumb}>
                <PageTitle
                    title={
                        courseTitle
                            ? CHEATSHEET_MESSAGES.cheatsheetOf(courseTitle)
                            : CHEATSHEET_MESSAGES.cheatsheet
                    }
                />
                {renderContent()}
            </AccountLayout>
        </>
    );
};

export default CheatsheetListPage;